import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Leaf, Zap, Flame, ArrowRight, Sparkles, Sun } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";
import { scrollToId } from "./Nav";

const ELEC_RATE = 0.14;
const ELEC_DAY = 0.155;
const ELEC_NIGHT = 0.12;
const GAS_RATE = 0.058;
const INJ_RATE = 0.04;
const FEES = { res: 65, soho: 95 };
const EAN_PROMO = { fix: 100, flex: 50 };

const REGION_FACTORS = {
  flanders: { elec: 1.0, gas: 1.0 },
  brussels: { elec: 0.95, gas: 0.98 },
  wallonia: { elec: 1.08, gas: 1.05 },
};

const cpRegion = (cp) => {
  const n = Number(cp);
  if (!/^\d{4}$/.test(cp) || n < 1000 || n > 9999) return null;
  if (n <= 1299) return "brussels";
  if (n <= 1499) return "wallonia";
  if (n <= 3999) return "flanders";
  if (n <= 7999) return "wallonia";
  return "flanders";
};

const clampKwh = (v, max) => Math.min(max, Math.max(0, Number(v) || 0));

export const EnecoTeaser = () => {
  const { t } = useLang();
  const e = t.enecoTeaser;
  const [mode, setMode] = useState("both");
  const [tariff, setTariff] = useState("fix");
  const [soho, setSoho] = useState(false);
  const [meter, setMeter] = useState("mono");
  const [elecKwh, setElecKwh] = useState(3500);
  const [dayKwh, setDayKwh] = useState(1750);
  const [nightKwh, setNightKwh] = useState(1750);
  const [gasKwh, setGasKwh] = useState(17000);
  const [solar, setSolar] = useState(false);
  const [injKwh, setInjKwh] = useState(2500);
  const [current, setCurrent] = useState("");
  const [cp, setCp] = useState("");
  const region = cpRegion(cp);

  const hasElec = mode !== "gas";
  const hasGas = mode !== "elec";

  const r = useMemo(() => {
    const f = REGION_FACTORS[region] || { elec: 1, gas: 1 };
    const FEE = soho ? FEES.soho : FEES.res;
    const elecCommodity = (meter === "bi" ? dayKwh * ELEC_DAY + nightKwh * ELEC_NIGHT : elecKwh * ELEC_RATE) * f.elec;
    const injCredit = hasElec && solar ? injKwh * INJ_RATE : 0;
    const elecYear = hasElec ? Math.max(0, elecCommodity - injCredit) + FEE : 0;
    const gasYear = hasGas ? gasKwh * GAS_RATE * f.gas + FEE : 0;
    const year = elecYear + gasYear;
    const eans = (hasElec ? 1 : 0) + (hasGas ? 1 : 0);
    const promo = eans * EAN_PROMO[tariff];
    const firstYear = Math.max(0, year - promo);
    return { elecYear, gasYear, year, month: year / 12, elecMonth: elecYear / 12, gasMonth: gasYear / 12, injCredit, promo, firstYear };
  }, [hasElec, hasGas, elecKwh, gasKwh, meter, dayKwh, nightKwh, solar, injKwh, region, soho, tariff]);

  const fmt = (n) => Math.round(n).toLocaleString("fr-BE");

  const currentMonthly = Number(current) || 0;
  const savingYear = currentMonthly > 0 ? currentMonthly * 12 - r.year : 0;

  const requestSim = () => {
    const parts = [];
    if (hasElec) {
      parts.push(meter === "bi"
        ? `${e.elecLine} ${e.meterBiShort} ${fmt(dayKwh)}/${fmt(nightKwh)} kWh/${e.yr}`
        : `${e.elecLine} ${fmt(elecKwh)} kWh/${e.yr}`);
      if (solar) parts.push(`${e.injLine} ${fmt(injKwh)} kWh/${e.yr}`);
    }
    if (hasGas) parts.push(`${e.gasLine} ${fmt(gasKwh)} kWh/${e.yr}`);
    parts.push(tariff === "fix" ? "Fix One" : "Flex One");
    if (soho) parts.push("SOHO");
    if (r.promo > 0) parts.push(`${e.promoShort} −€${r.promo}`);
    if (region) parts.push(`CP ${cp} (${e.regions[region]})`);
    const cur = currentMonthly > 0 ? ` (${e.currentShort} €${fmt(currentMonthly)}${e.perMonth})` : "";
    const label = `Eneco : ${parts.join(" + ")} ≈ €${fmt(r.month)}${e.perMonth}${cur}`;
    api.post("/simulations", { label: `Eneco ${mode}`, cta: true }).catch(() => {});
    sessionStorage.setItem("sta_sim", label);
    window.dispatchEvent(new CustomEvent("sta-sim", { detail: label }));
    scrollToId("contact");
  };

  const pill = (active) =>
    `px-4 py-2 rounded-full text-xs font-semibold border transition-colors duration-300 ${
      active ? "border-emerald-400 bg-emerald-500/15 text-emerald-400" : "border-white/15 text-white/50 hover:border-white/40"
    }`;

  const inputCls =
    "w-full bg-[#0a0a0a] border border-white/15 focus:border-emerald-400 outline-none px-4 py-3 text-white placeholder:text-white/30 transition-colors duration-300";

  return (
    <section id="energy" className="py-20 md:py-28 border-t border-white/10 bg-[#0d0d0d] overflow-hidden relative" data-testid="eneco-teaser">
      <div aria-hidden className="absolute -bottom-40 -left-40 w-[480px] h-[480px] rounded-full bg-emerald-500/10 blur-3xl" />
      <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="border border-emerald-500/25 bg-[#111111] p-8 md:p-14 grid lg:grid-cols-2 gap-10"
        >
          <div>
            <p className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-emerald-400 mb-5">
              <Leaf className="w-4 h-4" /> {e.overline}
            </p>
            <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mb-4">{e.title}</h2>
            <p className="text-white/60 text-sm md:text-base max-w-xl">{e.text}</p>
            <p className="mt-6 flex items-center gap-2 text-sm text-white/70">
              <Zap className="w-4 h-4 text-emerald-400" /> {e.free}
            </p>
            <div className="mt-6 bg-white rounded-sm px-3 py-1.5 inline-flex items-center gap-1.5" data-testid="eneco-section-logo">
              <img src="/eneco-bubbles.png" alt="Eneco" className="h-6" />
              <span className="font-bold text-lg leading-none" style={{ color: "#e2001a" }}>Eneco</span>
            </div>
          </div>

          <div className="bg-[#0a0a0a] border border-white/10 p-6 md:p-8" data-testid="eneco-simulator">
            <div className="flex flex-wrap gap-2 mb-6">
              <button onClick={() => setMode("both")} className={pill(mode === "both")} data-testid="eneco-sim-mode-both">
                {e.modeBoth}
              </button>
              <button onClick={() => setMode("elec")} className={pill(mode === "elec")} data-testid="eneco-sim-mode-elec">
                {e.modeElec}
              </button>
              <button onClick={() => setMode("gas")} className={pill(mode === "gas")} data-testid="eneco-sim-mode-gas">
                {e.modeGas}
              </button>
            </div>
            <div className="flex flex-wrap gap-2 mb-4" data-testid="eneco-sim-tariffs">
              <button onClick={() => setTariff("fix")} className={pill(tariff === "fix")} data-testid="eneco-sim-tariff-fix">
                {e.tariffFix}
              </button>
              <button onClick={() => setTariff("flex")} className={pill(tariff === "flex")} data-testid="eneco-sim-tariff-flex">
                {e.tariffFlex}
              </button>
              <button onClick={() => setSoho(!soho)} className={pill(soho)} data-testid="eneco-sim-soho-toggle">
                {e.sohoLabel}
              </button>
            </div>
            <p className="text-[11px] text-emerald-400/80 mb-5" data-testid="eneco-sim-promo-banner">{e.promoBanner}</p>
            <div className="mb-5">
              <label className="text-xs text-white/50 mb-2 block">{e.cpLabel}</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={4}
                value={cp}
                onChange={(ev) => setCp(ev.target.value.replace(/\D/g, "").slice(0, 4))}
                placeholder={e.cpPlaceholder}
                className={inputCls}
                data-testid="eneco-sim-cp-input"
              />
              {cp.length === 4 && !region && (
                <p className="mt-1.5 text-[11px] text-red-400" data-testid="eneco-sim-cp-invalid">{e.cpInvalid}</p>
              )}
              {region && (
                <p className="mt-1.5 text-[11px] text-emerald-400" data-testid="eneco-sim-region">
                  {e.regionLabel} {e.regions[region]} — {e.regionNote}
                </p>
              )}
            </div>
            {hasElec && (
              <div className="mb-5">
                <p className="text-xs text-white/50 mb-2">{e.meterLabel}</p>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => setMeter("mono")} className={pill(meter === "mono")} data-testid="eneco-sim-meter-mono">
                    {e.meterMono}
                  </button>
                  <button onClick={() => setMeter("bi")} className={pill(meter === "bi")} data-testid="eneco-sim-meter-bi">
                    {e.meterBi}
                  </button>
                  <button onClick={() => setSolar(!solar)} className={pill(solar)} data-testid="eneco-sim-solar-toggle">
                    <Sun className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
                    {e.solarLabel}
                  </button>
                </div>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              {hasElec && meter === "mono" && (
                <div>
                  <label className="flex items-center gap-1.5 text-xs text-white/50 mb-2">
                    <Zap className="w-3.5 h-3.5 text-emerald-400" /> {e.elecLabel}
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={elecKwh}
                    onChange={(ev) => setElecKwh(clampKwh(ev.target.value, 100000))}
                    className={inputCls}
                    data-testid="eneco-sim-elec-input"
                  />
                  <p className="mt-1.5 text-[11px] text-white/30">{e.avgElec}</p>
                </div>
              )}
              {hasElec && meter === "bi" && (
                <>
                  <div>
                    <label className="flex items-center gap-1.5 text-xs text-white/50 mb-2">
                      <Zap className="w-3.5 h-3.5 text-emerald-400" /> {e.dayLabel}
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={dayKwh}
                      onChange={(ev) => setDayKwh(clampKwh(ev.target.value, 100000))}
                      className={inputCls}
                      data-testid="eneco-sim-day-input"
                    />
                  </div>
                  <div>
                    <label className="flex items-center gap-1.5 text-xs text-white/50 mb-2">
                      <Zap className="w-3.5 h-3.5 text-emerald-400/60" /> {e.nightLabel}
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={nightKwh}
                      onChange={(ev) => setNightKwh(clampKwh(ev.target.value, 100000))}
                      className={inputCls}
                      data-testid="eneco-sim-night-input"
                    />
                    <p className="mt-1.5 text-[11px] text-white/30">{e.nightHint}</p>
                  </div>
                </>
              )}
              {hasGas && (
                <div>
                  <label className="flex items-center gap-1.5 text-xs text-white/50 mb-2">
                    <Flame className="w-3.5 h-3.5 text-emerald-400" /> {e.gasLabel}
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={gasKwh}
                    onChange={(ev) => setGasKwh(clampKwh(ev.target.value, 200000))}
                    className={inputCls}
                    data-testid="eneco-sim-gas-input"
                  />
                  <p className="mt-1.5 text-[11px] text-white/30">{e.avgGas}</p>
                </div>
              )}
              {hasElec && solar && (
                <div>
                  <label className="flex items-center gap-1.5 text-xs text-white/50 mb-2">
                    <Sun className="w-3.5 h-3.5 text-amber-400" /> {e.injLabel}
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={injKwh}
                    onChange={(ev) => setInjKwh(clampKwh(ev.target.value, 100000))}
                    className={inputCls}
                    data-testid="eneco-sim-inj-input"
                  />
                  <p className="mt-1.5 text-[11px] text-white/30">{e.injHint}</p>
                </div>
              )}
            </div>
            <div className="mt-4">
              <label className="text-xs text-white/50 mb-2 block">{e.currentLabel}</label>
              <input
                type="number"
                min="0"
                value={current}
                onChange={(ev) => setCurrent(ev.target.value)}
                placeholder={e.currentPlaceholder}
                className={inputCls}
                data-testid="eneco-sim-current-input"
              />
            </div>
            <div className="mt-6 border-t border-white/10 pt-5" data-testid="eneco-sim-result">
              <p className="text-[11px] uppercase tracking-widest text-white/40 mb-2">{e.result}</p>
              {hasElec && (
                <p className="flex justify-between text-sm text-white/60">
                  <span>{e.elecLine}</span>
                  <span>€{fmt(r.elecMonth)}{e.perMonth}</span>
                </p>
              )}
              {hasElec && solar && r.injCredit > 0 && (
                <p className="flex justify-between text-sm text-amber-400/80 mt-1" data-testid="eneco-sim-inj-line">
                  <span>{e.injLine}</span>
                  <span>−€{fmt(r.injCredit)}/{e.yr}</span>
                </p>
              )}
              {hasGas && (
                <p className="flex justify-between text-sm text-white/60 mt-1">
                  <span>{e.gasLine}</span>
                  <span>€{fmt(r.gasMonth)}{e.perMonth}</span>
                </p>
              )}
              <p className="mt-3 font-display text-4xl font-semibold text-emerald-400">
                €{fmt(r.month)}
                <span className="text-base font-normal text-white/50">{e.perMonth}</span>
              </p>
              <p className="text-xs text-white/40 mt-1">≈ €{fmt(r.year)}/{e.yr} · {soho ? e.feeNoteSoho : e.feeNote}</p>
              {r.promo > 0 && (
                <p className="mt-2 flex justify-between text-sm text-emerald-400" data-testid="eneco-sim-promo-line">
                  <span>{e.promoLine.replace("{a}", EAN_PROMO[tariff])}</span>
                  <span>−€{r.promo}</span>
                </p>
              )}
              {r.promo > 0 && (
                <p className="text-xs text-white/50 mt-1" data-testid="eneco-sim-first-year">
                  {e.firstYear.replace("{y}", fmt(r.firstYear)).replace("{m}", fmt(r.firstYear / 12))}
                </p>
              )}
              {currentMonthly > 0 && savingYear > 0 && (
                <p className="mt-3 flex items-center gap-2 border border-emerald-500/40 bg-emerald-500/10 px-3 py-2.5 text-sm font-semibold text-emerald-400" data-testid="eneco-sim-saving">
                  <Sparkles className="w-4 h-4 shrink-0" />
                  {e.saving.replace("{y}", fmt(savingYear)).replace("{m}", fmt(savingYear / 12))}
                </p>
              )}
              {currentMonthly > 0 && savingYear <= 0 && (
                <p className="mt-3 text-xs text-white/40" data-testid="eneco-sim-no-saving">{e.noSaving}</p>
              )}
              <p className="text-[11px] text-white/30 mt-3 leading-relaxed">{e.disclaimer}</p>
              <button
                onClick={requestSim}
                data-testid="eneco-sim-cta"
                className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-white hover:text-black text-white text-sm font-semibold px-6 py-3.5 rounded-full transition-colors duration-300"
              >
                <Sparkles className="w-4 h-4" />
                {e.cta}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
